import { computed, onUnmounted, type Ref, watch } from 'vue'

import { useUserInfoLoaded } from '@/composables/use-user-state'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useLoginNudgeStore } from '@/store/login-nudge-store'
import { SIGN_IN_MODAL_WEIGHT, useNotificationsQueue } from '@/store/notification-que/notifications-queue'
import { useUserStore } from '@/store/user-store/user-store'

import type { LoginPromptReason } from './use-login-prompt-trigger'

const NOTIFICATION = 'sign-in-modal'
const ONE_TAP_NOTIFICATION = 'auth-notification'
// plenty of games report neither a reward nor a completed level, so a long session earns one nudge on its own
const NUDGE_AFTER_PLAYTIME_MS = 10 * 60 * 1000
// the cooldown is spent in coarse steps so the persisted store is not rewritten on every tick of play
const COOLDOWN_STEP_MS = 15 * 1000

const ourSource = (reason: LoginPromptReason) => `game-${reason}`

/** Nudges a guest to sign in right after they got ahead in the game: "log in to keep the progress". */
export function useLoginNudge(
    trigger: Ref<{ reason: LoginPromptReason; seq: number } | undefined>,
    game: {
        /** Bumped when a fullscreen ad claims the screen. */
        adStarted: Ref<number>
        /** Milliseconds of play with the tab visible - it stands still while nobody is playing. */
        playedMs: Ref<number>
        /** The trigger's request - it parks the nudge while an ad is up and releases it when the ad ends. */
        request: (reason: LoginPromptReason) => void
    },
) {
    const userStore = useUserStore()
    const layoutStore = useLayoutStore()
    const loginNudgeStore = useLoginNudgeStore()
    const loggerStore = useLoggerStore()
    const notificationsQueue = useNotificationsQueue()
    const userInfoLoaded = useUserInfoLoaded()

    let shownReason: LoginPromptReason | null = null
    let hasShownThisSession = false
    let hasAskedForPlaytime = false
    let heldReason: LoginPromptReason | null = null
    let hasLoggedThisEpisode = false
    let cooldownFedAtMs = 0
    // the close watcher runs a flush later than the retraction, so this is consumed there rather than reset here
    let skipCooldownStamp = false

    function canPrompt() {
        return userInfoLoaded.value && !userStore.isAuthorized
    }

    /*
      One tap outranks us in the queue, so a request made while it is around is dropped and never retried.
      A queued item is invisible in activeName until init(), so look for it in the queue itself.
    */
    const isOneTapPending = computed(() =>
        notificationsQueue.notificationsQueue.some((item) => item.name === ONE_TAP_NOTIFICATION),
    )

    function requestNudge(reason: LoginPromptReason) {
        if (!canPrompt() || loginNudgeStore.isNudgeCoolingDown()) {
            return
        }

        // hold the moment rather than spend it: one tap is asking the same question right now
        if (isOneTapPending.value) {
            heldReason = reason
            return
        }

        const source = ourSource(reason)

        notificationsQueue.request({
            name: NOTIFICATION,
            weight: SIGN_IN_MODAL_WEIGHT,
            onOpen: () => {
                if (!canPrompt() || layoutStore.signInModalOpened) {
                    notificationsQueue.remove(NOTIFICATION)
                    return
                }

                shownReason = reason
                hasShownThisSession = true
                layoutStore.openSignInModal(source, { saveVariant: true })

                if (!hasLoggedThisEpisode) {
                    hasLoggedThisEpisode = true
                    loggerStore.logEvent({
                        event: 'custom_event',
                        eventName: 'auth_popup_auto_opened',
                        dimension1: source,
                    })
                }
            },
            onClose: () => {
                if (layoutStore.signInModalOpenedSource === source) {
                    layoutStore.closeSignInModal()
                }
            },
        })
    }

    watch(trigger, (value) => {
        if (value) {
            requestNudge(value.reason)
        }
    })

    // one tap freed the queue - the moment we held is still worth asking about
    watch(isOneTapPending, (pending) => {
        if (!pending && heldReason) {
            const reason = heldReason
            heldReason = null
            requestNudge(reason)
        }
    })

    /*
      The game can ask for an ad at any point after level_completed, so the modal may already be up when the ad
      claims the screen - and the modal lives in the dialog top layer, which would leave the ad playing behind an
      unclickable backdrop. Hand the screen over and come back once the ad is gone.
    */
    watch(game.adStarted, () => {
        if (!shownReason || layoutStore.signInModalOpenedSource !== ourSource(shownReason)) {
            return
        }

        const reason = shownReason
        skipCooldownStamp = true
        notificationsQueue.remove(NOTIFICATION)
        game.request(reason)
    })

    watch(
        () => layoutStore.signInModalOpened,
        (opened) => {
            if (opened) {
                return
            }

            /*
              One tap loads late and outranks us, so it can force close a modal that is already up. That is a
              preemption, not the player dismissing us: keep the moment for after the queue frees up. The queue
              clears us before it pushes one tap, so this is the first point that can tell the two apart.
            */
            if (shownReason && isOneTapPending.value) {
                heldReason = shownReason
            } else if (shownReason && !skipCooldownStamp) {
                loginNudgeStore.markNudgeShown()
                hasLoggedThisEpisode = false
            }
            skipCooldownStamp = false
            shownReason = null
            notificationsQueue.remove(NOTIFICATION)
        },
    )

    watch(game.playedMs, (playedMs) => {
        if (playedMs - cooldownFedAtMs >= COOLDOWN_STEP_MS) {
            loginNudgeStore.consumePlaytime(playedMs - cooldownFedAtMs)
            cooldownFedAtMs = playedMs
        }

        // a game that reports neither reward nor level_completed would never nudge at all, so playing earns one
        if (!hasShownThisSession && !hasAskedForPlaytime && playedMs >= NUDGE_AFTER_PLAYTIME_MS) {
            hasAskedForPlaytime = true
            game.request('playtime')
        }
    })

    onUnmounted(() => notificationsQueue.remove(NOTIFICATION))
}
